import type { RegisteredComponent } from "@builder.io/sdk-react";

import { TreatmentPrice } from "~/hec/telehealth/components/treatment-price";
import { useOptionalTelehealthPdpPrice } from "~/hec/telehealth/pdp-price-context";
import type { PriceDisplay } from "~/shop/utils/price-display";
import { wasNowDisplay } from "~/shop/utils/price-display";

export interface Glp1PricingProps {
  /**
   * Render the live catalog price for the product on screen. Default ON so an
   * entry created without touching pricing behaves exactly like the coded
   * column it supersedes.
   */
  useDefaultPrice?: boolean;
  /** Regular (pre-discount) price. Struck through when a discount is set. */
  regularPrice?: number;
  /** First-month / promotional price. Shown as the emphasised "now" price. */
  discountedPrice?: number;
  /**
   * e.g. "Starts at" — rendered before the price.
   *
   * Ignored whenever a discount is showing (ET-394). "Starts at" describes the
   * floor of a range; a was/now pair already says what the offer is, so the
   * combination rendered `Starts at ~~$499/mo~~ $475` — a string no one
   * approved. Suppressed at render rather than only hidden in the editor,
   * because `showIf` doesn't clear a value an entry already carries.
   */
  pricePrefix?: string;
  /**
   * Recurring-period suffix for MANUAL mode. Rendered against the regular price
   * only, matching ET-220/221 (`~~$499/mo~~ $475`) — the discounted figure here
   * is a one-off first-month charge, so it isn't "/mo". Default mode takes its
   * suffix from the live price context instead, where a discount is a catalog
   * markdown and both figures recur.
   */
  pricePeriod?: string;
  /**
   * e.g. "1st month" — a qualifier after the price, giving ET-394's
   * `Starts at $475 1st month`.
   *
   * Available in BOTH modes, unlike `pricePeriod`. The period is gated to
   * manual mode because default mode has a competing live source for it
   * (`live.period`); nothing supplies a qualifier, so there is nothing for an
   * authored one to contradict.
   */
  priceSuffix?: string;
  attributes?: Record<string, unknown>;
}

/**
 * ET-216 — the GLP-1 pricing block for the treatment PDP's Builder section.
 *
 * Two modes, one field:
 *  - `useDefaultPrice` (default): the live catalog price, read from the buy
 *    column's price context so it goes through the same
 *    `resolvePriceFromSelectedVariant` + `telehealthPricePeriod` path the coded
 *    column uses. Responds to the AB pricebook and to promotions.
 *  - manual: `regularPrice` / `discountedPrice`, for the cases the catalog can't
 *    express yet (ET-220/221's first-month discount, ET-222/223's "Starts at").
 *
 * The affixes make ET-394's two approved shapes a per-SKU authoring choice
 * rather than a code change: `~~$499/mo~~ $475` (regular + discounted +
 * period) or `Starts at $475 1st month` (regular + prefix + suffix, no
 * period). They are mutually exclusive by construction — a discount hides the
 * prefix.
 *
 * A manual price is display-only, and nothing on this page can act on it: the
 * treatment PDP has no Add to Cart — "Get Started" routes to intake — so the
 * basket is never touched here. The figure a shopper is charged comes from the
 * plan-selection screen after intake, off SCAPI variant pricing. Worth keeping
 * the two consistent so the price doesn't appear to change mid-journey, but this
 * block cannot mis-charge anyone.
 *
 * `noWrap` + spread `attributes` per the Builder convention.
 */
export function Glp1Pricing({
  useDefaultPrice = true,
  regularPrice,
  discountedPrice,
  pricePrefix,
  pricePeriod,
  priceSuffix,
  attributes,
}: Glp1PricingProps) {
  const live = useOptionalTelehealthPdpPrice();

  // Default mode outside a treatment PDP has no catalog product to read —
  // render nothing rather than an empty price row.
  const manual: PriceDisplay | null =
    wasNowDisplay(regularPrice, discountedPrice) ??
    (typeof regularPrice === "number"
      ? { price: regularPrice, isOnSale: false, isRange: false }
      : null);

  const display = useDefaultPrice ? (live?.display ?? null) : manual;
  const period = useDefaultPrice
    ? (live?.period ?? null)
    : (pricePeriod ?? null);

  if (!display) return null;

  return (
    <div {...attributes} data-testid="pdp-glp1-pricing">
      {/* The mode IS the discount's provenance, which is the signal
          `TreatmentPrice` can't recover on its own. Manual = the one-off
          first-month charge this mode exists to express (`~~$499/mo~~ $475`);
          default = a catalog markdown, where both figures recur. */}
      <TreatmentPrice
        display={display}
        period={period}
        periodScope={useDefaultPrice ? "both" : "regular"}
        // Gated on `isOnSale` rather than on `discountedPrice` so it also
        // covers DEFAULT mode, where a catalog markdown (AB pricebook /
        // promotion, via `c_priceInfo`) puts a was/now on screen with no
        // authored discount at all — which is where the reported
        // `Starts at ~~$300/mo~~ $134` came from.
        prefix={display.isOnSale ? undefined : pricePrefix}
        trailing={priceSuffix}
      />
    </div>
  );
}

export const Glp1PricingDefinition = {
  component: Glp1Pricing,
  name: "GLP1Pricing",
  noWrap: true,
  inputs: [
    {
      name: "useDefaultPrice",
      type: "boolean",
      defaultValue: true,
      helperText:
        "Show the live catalog price for this product (recommended). Turn off only to enter a price the catalog can't express yet.",
    },
    {
      name: "regularPrice",
      type: "number",
      showIf: `options.get('useDefaultPrice') !== true`,
      helperText:
        "Regular price. Struck through when a discounted price is also set.",
    },
    {
      name: "discountedPrice",
      type: "number",
      showIf: `options.get('useDefaultPrice') !== true`,
      helperText:
        "First-month / promotional price. DISPLAY ONLY — it does not change what the basket charges.",
    },
    {
      name: "pricePrefix",
      type: "string",
      // Hidden once a MANUAL discount is entered: the was/now pair already
      // states the offer, and the two together read as `Starts at $499/mo
      // $475`. Qualified by the mode because `discountedPrice` is itself
      // mode-gated — without that clause, a stale value left behind after
      // flipping back to default mode would hide this field for no reason an
      // author can see. Default mode can't be gated here at all (the editor
      // doesn't know whether the live price is on sale), so the component
      // suppresses that case at render — see `Glp1PricingProps`.
      showIf: `options.get('useDefaultPrice') === true || !options.get('discountedPrice')`,
      helperText:
        'Optional text before the price, e.g. "Starts at". Ignored whenever a discount is showing — a struck was/now price already states the offer.',
    },
    {
      name: "pricePeriod",
      type: "string",
      showIf: `options.get('useDefaultPrice') !== true`,
      defaultValue: "/mo",
      helperText:
        'Suffix on the regular price, e.g. "/mo". Leave empty for a one-off price.',
    },
    {
      name: "priceSuffix",
      type: "string",
      helperText:
        'Optional text after the price, e.g. "1st month". For "Starts at $475 1st month", clear Price Period as well — otherwise it renders "$475/mo 1st month".',
    },
  ],
} satisfies RegisteredComponent;
